import React from 'react';
import { Plane, Briefcase, RefreshCw, Info, FileText, Upload, CreditCard, CheckCircle, Clock, Send, Mail, Phone, Users, Shield, Package, Star, Building, Building2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Plane: Plane,
  Briefcase: Briefcase,
  RefreshCw: RefreshCw,
  Users: Users,
  Building: Building,
  Building2: Building2,
};

// Icon map for steps
const stepIconMap = {
  FileText: FileText,
  Upload: Upload,
  CreditCard: CreditCard,
  CheckCircle: CheckCircle,
  Clock: Clock,
  Send: Send,
  Mail: Mail,
  Phone: Phone,
  Users: Users,
  Shield: Shield,
  Package: Package,
  Star: Star
};

// Service translation mapping
const serviceTranslationMap = {
  "Dubai Visa 14 Days": { nameKey: "dubaiVisa14Days", descKey: "desc14Days" },
  "Dubai Visa 30 Days": { nameKey: "dubaiVisa30Days", descKey: "desc30Days" },
  "Dubai Visa 60 Days": { nameKey: "dubaiVisa60Days", descKey: "desc60Days" },
  "Dubai Visa": { nameKey: "dubaiVisa30Days", descKey: "desc30Days" },
  "Freelance Visa": { nameKey: "freelanceVisaName", descKey: "descFreelance" },
  "30 Days Visa Change": { nameKey: "visaChange30Days", descKey: "descVisaChange30" },
  "60 Days Visa Change": { nameKey: "visaChange60Days", descKey: "descVisaChange60" },
  "Visa Change": { nameKey: "visaChange60Days", descKey: "descVisaChange60" },
  "Family Visa": { nameKey: "familyVisaName", descKey: "descFamilyVisa" }
};

const subtitleTranslationMap = {
  "Dubai Visa": "touristVisa",
  "DUBAI VISA": "touristVisa",
  "Tourist Visa": "touristVisa",
  "Toursit Visa": "touristVisa",
  "TOURIST VISA": "touristVisa",
  "TOURSIT VISA": "touristVisa",
  "Freelance Visa": "freelanceVisaName",
  "Visa Change": "visaChange60Days",
  "VISA CHANGE": "visaChange60Days",
  "Family Visa": "familyVisaName",
  "FAMILY VISA": "familyVisaName",
  "Sponsor Visa": "familyVisaName"
};

// Helper function to get the correct image URL
const getImageUrl = (image) => {
  if (!image) return null;
  // If it's already a full URL, use it directly
  if (image.startsWith('http://') || image.startsWith('https://')) {
    return image;
  }
  // Otherwise, it's a local file - prepend the backend URL
  return `${BACKEND_URL}/api/uploads/${image}`;
};

const ServiceCard = ({ service, onBookClick, onStepsClick }) => {
  const IconComponent = iconMap[service.icon] || Plane;
  const { t, language } = useLanguage();
  
  // Get translated service data — always prefer the live DB value so admin
  // edits in the Admin Panel flow through immediately. The translation map
  // is now ONLY a last-resort fallback when the DB field is empty.
  const getTranslatedName = () => {
    if (language === 'ru' && service.name_ru) return service.name_ru;
    if (language === 'uz' && service.name_uz) return service.name_uz;
    // English (or DB has no localized variant) → use DB name directly
    if (service.name) return service.name;
    // Final fallback: hardcoded translation map (only triggers if DB empty)
    const translationKeys = serviceTranslationMap[service.name];
    return translationKeys ? t(translationKeys.nameKey) : service.name;
  };
  
  const getTranslatedDesc = () => {
    if (language === 'ru' && service.description_ru) return service.description_ru;
    if (language === 'uz' && service.description_uz) return service.description_uz;
    // English (or DB has no localized variant) → use DB description directly
    if (service.description) return service.description;
    // Final fallback: hardcoded translation map (only triggers if DB empty)
    const translationKeys = serviceTranslationMap[service.name];
    return translationKeys ? t(translationKeys.descKey) : service.description;
  };
  
  const translatedName = getTranslatedName();
  const translatedDesc = getTranslatedDesc();
  const translatedSubtitle = (() => {
    if (!service.subtitle) return null;
    // 1) Prefer admin-managed DB translation
    if (language === 'ru' && service.subtitle_ru) return service.subtitle_ru;
    if (language === 'uz' && service.subtitle_uz) return service.subtitle_uz;
    // 2) Fallback to hardcoded translation map
    const key = subtitleTranslationMap[service.subtitle] || subtitleTranslationMap[service.subtitle.toUpperCase()];
    return key ? t(key) : service.subtitle;
  })();
  
  // Translate duration
  const translateDuration = (duration) => {
    // 1) Prefer admin-managed DB translation
    if (language === 'ru' && service.duration_ru) return service.duration_ru;
    if (language === 'uz' && service.duration_uz) return service.duration_uz;
    // 2) Fallback to keyword-based translation
    if (duration.includes('business days')) {
      const num = duration.match(/\d+-?\d*/)?.[0] || '';
      return `${num} ${t('businessDays')}`;
    } else if (duration.includes('hours')) {
      const num = duration.match(/\d+/)?.[0] || '';
      return `${num} ${t('hours')}`;
    }
    return duration;
  };

  const hasSteps = service.steps && service.steps.length > 0;
  const imageUrl = getImageUrl(service.image);
  const isDubaiVisa = service.name?.toLowerCase().includes('dubai') || 
    (service.name?.toLowerCase().includes('visa') && !service.name?.toLowerCase().includes('change') && !service.name?.toLowerCase().includes('freelance') && !service.name?.toLowerCase().includes('family'));
  const isVisaChange = service.name?.toLowerCase().includes('change');
  const isFreelance = service.name?.toLowerCase().includes('freelance');
  const isFamilyVisa = service.name?.toLowerCase().includes('family');
  const isBusiness = service.zone === 'freezone' || service.zone === 'mainland' ||
    (service.category || '').toLowerCase() === 'business' ||
    /mainland|free\s*zone|company/.test((service.name || '').toLowerCase());
  const showStepsButton = hasSteps || isDubaiVisa;

  return (
    <div className="service-card-new">
      {isDubaiVisa ? (
        <div className="service-image-container visa-animation-container">
          <div className="visa-animation">
            <div className="visa-bg-gradient"></div>
            <svg className="visa-skyline" viewBox="0 0 400 120" preserveAspectRatio="none">
              <rect x="30" y="40" width="18" height="80" rx="2" fill="rgba(0,212,170,0.15)" />
              <rect x="55" y="55" width="14" height="65" rx="2" fill="rgba(0,212,170,0.1)" />
              <rect x="75" y="25" width="12" height="95" rx="1" fill="rgba(0,212,170,0.18)" />
              <rect x="92" y="60" width="16" height="60" rx="2" fill="rgba(0,212,170,0.12)" />
              <rect x="115" y="10" width="8" height="110" rx="1" fill="rgba(0,212,170,0.2)" />
              <rect x="113" y="8" width="12" height="4" rx="1" fill="rgba(0,212,170,0.25)" />
              <rect x="132" y="45" width="15" height="75" rx="2" fill="rgba(0,212,170,0.13)" />
              <rect x="155" y="30" width="20" height="90" rx="2" fill="rgba(0,212,170,0.16)" />
              <rect x="182" y="50" width="12" height="70" rx="2" fill="rgba(0,212,170,0.11)" />
              <rect x="200" y="20" width="16" height="100" rx="2" fill="rgba(0,212,170,0.17)" />
              <rect x="223" y="60" width="18" height="60" rx="2" fill="rgba(0,212,170,0.1)" />
              <rect x="248" y="35" width="14" height="85" rx="2" fill="rgba(0,212,170,0.14)" />
              <rect x="270" y="50" width="20" height="70" rx="2" fill="rgba(0,212,170,0.12)" />
              <rect x="298" y="15" width="10" height="105" rx="1" fill="rgba(0,212,170,0.19)" />
              <rect x="296" y="12" width="14" height="5" rx="1" fill="rgba(0,212,170,0.22)" />
              <rect x="318" y="45" width="16" height="75" rx="2" fill="rgba(0,212,170,0.13)" />
              <rect x="342" y="55" width="14" height="65" rx="2" fill="rgba(0,212,170,0.1)" />
              <rect x="363" y="40" width="18" height="80" rx="2" fill="rgba(0,212,170,0.15)" />
            </svg>
            <svg className="visa-plane" viewBox="0 0 40 40">
              <path d="M35 20L5 8l5 10H25l-15 4 5 10z" fill="#00D4AA" opacity="0.9"/>
            </svg>
            <div className="visa-passport">
              <div className="passport-inner">
                <div className="passport-emblem"></div>
                <div className="passport-text">VISA</div>
                <div className="passport-line"></div>
                <div className="passport-line short"></div>
              </div>
            </div>
            <div className="visa-stamp">
              <span>UAE</span>
            </div>
            <div className="visa-dots">
              <div className="visa-dot d1"></div>
              <div className="visa-dot d2"></div>
              <div className="visa-dot d3"></div>
              <div className="visa-dot d4"></div>
              <div className="visa-dot d5"></div>
            </div>
          </div>
          <div className="service-image-overlay"></div>
        </div>
      ) : isVisaChange ? (
        <div className="service-image-container visa-change-animation-container">
          <div className="vc-animation">
            <div className="vc-bg-gradient"></div>
            {/* Dubai skyline background */}
            <svg className="vc-skyline" viewBox="0 0 400 80" preserveAspectRatio="none">
              <rect x="20" y="20" width="16" height="60" rx="2" fill="rgba(0,212,170,0.1)" />
              <rect x="42" y="30" width="12" height="50" rx="1" fill="rgba(0,212,170,0.08)" />
              <rect x="60" y="10" width="8" height="70" rx="1" fill="rgba(0,212,170,0.12)" />
              <rect x="58" y="7" width="12" height="4" rx="1" fill="rgba(0,212,170,0.15)" />
              <rect x="80" y="25" width="14" height="55" rx="2" fill="rgba(0,212,170,0.09)" />
              <rect x="100" y="35" width="18" height="45" rx="2" fill="rgba(0,212,170,0.07)" />
              <rect x="200" y="15" width="10" height="65" rx="1" fill="rgba(0,212,170,0.11)" />
              <rect x="220" y="28" width="16" height="52" rx="2" fill="rgba(0,212,170,0.08)" />
              <rect x="320" y="20" width="14" height="60" rx="2" fill="rgba(0,212,170,0.1)" />
              <rect x="340" y="12" width="8" height="68" rx="1" fill="rgba(0,212,170,0.13)" />
              <rect x="360" y="30" width="18" height="50" rx="2" fill="rgba(0,212,170,0.08)" />
            </svg>
            {/* Labels: Dubai (left) - Oman (right) */}
            <div className="vc-labels">
              <span className="vc-label vc-label-left">DUBAI</span>
              <span className="vc-label vc-label-right">OMAN</span>
            </div>
            {/* Road */}
            <div className="vc-road">
              <div className="vc-road-line vc-rl-1"></div>
              <div className="vc-road-line vc-rl-2"></div>
              <div className="vc-road-line vc-rl-3"></div>
              <div className="vc-road-line vc-rl-4"></div>
              <div className="vc-road-line vc-rl-5"></div>
            </div>
            {/* Car with people */}
            <svg className="vc-car" viewBox="0 0 80 36">
              {/* Car body */}
              <rect x="5" y="14" width="70" height="16" rx="5" fill="#00D4AA" opacity="0.85"/>
              {/* Car roof/cabin */}
              <path d="M20 14 L26 4 L58 4 L64 14" fill="#00D4AA" opacity="0.65" stroke="rgba(0,212,170,0.9)" strokeWidth="0.5"/>
              {/* Windshield */}
              <path d="M22 14 L27 5.5 L37 5.5 L37 14" fill="rgba(100,220,255,0.2)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
              {/* Rear window */}
              <path d="M47 5.5 L57 5.5 L62 14 L47 14" fill="rgba(100,220,255,0.2)" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5"/>
              {/* Side window */}
              <rect x="38" y="5.5" width="8" height="8.5" fill="rgba(100,220,255,0.15)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
              {/* Driver - head */}
              <circle cx="32" cy="8" r="2.5" fill="rgba(255,220,185,0.7)"/>
              {/* Driver - body */}
              <rect x="30" y="10.5" width="4" height="3.5" rx="1" fill="rgba(0,180,140,0.6)"/>
              {/* Passenger - head */}
              <circle cx="52" cy="8" r="2.5" fill="rgba(255,220,185,0.7)"/>
              {/* Passenger - body */}
              <rect x="50" y="10.5" width="4" height="3.5" rx="1" fill="rgba(60,130,200,0.6)"/>
              {/* Headlights */}
              <rect x="1" y="18" width="6" height="3" rx="1.5" fill="#ffcc00" opacity="0.6"/>
              <circle cx="3" cy="19.5" r="2" fill="#ffee88" opacity="0.3"/>
              {/* Tail lights */}
              <rect x="73" y="18" width="5" height="3" rx="1.5" fill="#ff4444" opacity="0.6"/>
              {/* Wheels */}
              <circle cx="22" cy="32" r="5" fill="rgba(255,255,255,0.25)"/>
              <circle cx="22" cy="32" r="3" fill="#0a1628"/>
              <circle cx="22" cy="32" r="1.5" fill="rgba(255,255,255,0.15)"/>
              <circle cx="60" cy="32" r="5" fill="rgba(255,255,255,0.25)"/>
              <circle cx="60" cy="32" r="3" fill="#0a1628"/>
              <circle cx="60" cy="32" r="1.5" fill="rgba(255,255,255,0.15)"/>
              {/* Side mirror */}
              <rect x="18" y="13" width="3" height="2" rx="1" fill="rgba(0,212,170,0.5)"/>
            </svg>
            {/* Bus with passengers */}
            <svg className="vc-bus" viewBox="0 0 90 40">
              {/* Bus body */}
              <rect x="3" y="8" width="84" height="24" rx="4" fill="rgba(0,212,170,0.7)"/>
              {/* Bus roof */}
              <rect x="6" y="4" width="78" height="8" rx="3" fill="rgba(0,212,170,0.5)"/>
              {/* Windows with passengers */}
              <rect x="10" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.2)"/>
              <circle cx="14.5" cy="9.5" r="1.8" fill="rgba(255,220,185,0.6)"/>
              <rect x="22" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.2)"/>
              <circle cx="26.5" cy="9.5" r="1.8" fill="rgba(255,220,185,0.6)"/>
              <rect x="34" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.2)"/>
              <circle cx="38.5" cy="9.5" r="1.8" fill="rgba(255,220,185,0.5)"/>
              <rect x="46" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.2)"/>
              <circle cx="50.5" cy="9.5" r="1.8" fill="rgba(255,220,185,0.6)"/>
              <rect x="58" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.2)"/>
              <circle cx="62.5" cy="9.5" r="1.8" fill="rgba(255,220,185,0.5)"/>
              <rect x="70" y="6" width="9" height="6" rx="1" fill="rgba(100,220,255,0.15)"/>
              {/* Door */}
              <rect x="5" y="10" width="4" height="14" rx="1" fill="rgba(0,180,140,0.4)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5"/>
              {/* Wheels */}
              <circle cx="20" cy="34" r="5" fill="rgba(255,255,255,0.25)"/>
              <circle cx="20" cy="34" r="3" fill="#0a1628"/>
              <circle cx="20" cy="34" r="1.5" fill="rgba(255,255,255,0.15)"/>
              <circle cx="70" cy="34" r="5" fill="rgba(255,255,255,0.25)"/>
              <circle cx="70" cy="34" r="3" fill="#0a1628"/>
              <circle cx="70" cy="34" r="1.5" fill="rgba(255,255,255,0.15)"/>
              {/* Headlights */}
              <rect x="85" y="16" width="4" height="3" rx="1.5" fill="#ffcc00" opacity="0.5"/>
            </svg>
            {/* Airplane */}
            <svg className="vc-plane" viewBox="0 0 40 40">
              <path d="M35 20L5 8l5 10H25l-15 4 5 10z" fill="#00D4AA" opacity="0.8"/>
            </svg>
            {/* Visa change arrows */}
            <div className="vc-arrows">
              <svg className="vc-arrow vc-arrow-1" viewBox="0 0 30 30">
                <path d="M5 15h16M16 10l5 5-5 5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
              <svg className="vc-arrow vc-arrow-2" viewBox="0 0 30 30">
                <path d="M25 15H9M14 10l-5 5 5 5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
            </div>
            {/* Floating dots */}
            <div className="visa-dots">
              <div className="visa-dot d1"></div>
              <div className="visa-dot d2"></div>
              <div className="visa-dot d3"></div>
              <div className="visa-dot d4"></div>
            </div>
          </div>
          <div className="service-image-overlay"></div>
        </div>
      ) : isFreelance ? (
        <div className="service-image-container fl-animation-container">
          <div className="fl-animation">
            <div className="fl-bg-gradient"></div>
            {/* Person at desk with laptop */}
            <svg className="fl-person" viewBox="0 0 80 70">
              {/* Desk */}
              <rect x="10" y="48" width="60" height="3" rx="1" fill="rgba(0,212,170,0.25)"/>
              <rect x="14" y="51" width="3" height="18" fill="rgba(0,212,170,0.15)"/>
              <rect x="63" y="51" width="3" height="18" fill="rgba(0,212,170,0.15)"/>
              {/* Laptop base */}
              <rect x="22" y="42" width="36" height="6" rx="2" fill="rgba(0,212,170,0.4)"/>
              {/* Laptop screen */}
              <rect x="25" y="22" width="30" height="20" rx="2" fill="rgba(0,212,170,0.2)" stroke="rgba(0,212,170,0.4)" strokeWidth="1"/>
              {/* Screen content - code lines */}
              <rect x="28" y="26" width="14" height="1.5" rx="0.5" fill="rgba(0,212,170,0.5)"/>
              <rect x="28" y="30" width="20" height="1.5" rx="0.5" fill="rgba(0,212,170,0.3)"/>
              <rect x="28" y="34" width="10" height="1.5" rx="0.5" fill="rgba(0,212,170,0.4)"/>
              <rect x="28" y="38" width="16" height="1.5" rx="0.5" fill="rgba(0,212,170,0.3)"/>
              {/* Person - head */}
              <circle cx="40" cy="12" r="6" fill="rgba(255,220,185,0.6)"/>
              {/* Person - body */}
              <rect x="34" y="18" width="12" height="10" rx="3" fill="rgba(0,180,140,0.5)"/>
              {/* Person - arms */}
              <line x1="34" y1="22" x2="28" y2="36" stroke="rgba(255,220,185,0.4)" strokeWidth="2.5" strokeLinecap="round"/>
              <line x1="46" y1="22" x2="52" y2="36" stroke="rgba(255,220,185,0.4)" strokeWidth="2.5" strokeLinecap="round"/>
            </svg>
            {/* Emirates ID card floating */}
            <div className="fl-license">
              <div className="fl-license-inner">
                <div className="fl-id-photo"></div>
                <div className="fl-id-right">
                  <div className="fl-license-text">EMIRATES ID</div>
                  <div className="fl-license-line"></div>
                  <div className="fl-license-line short"></div>
                  <div className="fl-license-line"></div>
                </div>
              </div>
            </div>
            {/* WiFi signal */}
            <svg className="fl-wifi" viewBox="0 0 30 24">
              <path d="M15 22 a1 1 0 1 0 0.01 0" fill="#00D4AA" opacity="0.7"/>
              <path d="M9 17 a8.5 8.5 0 0 1 12 0" fill="none" stroke="#00D4AA" strokeWidth="2" strokeLinecap="round" opacity="0.5"/>
              <path d="M5 13 a14 14 0 0 1 20 0" fill="none" stroke="#00D4AA" strokeWidth="2" strokeLinecap="round" opacity="0.35"/>
              <path d="M1 9 a19.5 19.5 0 0 1 28 0" fill="none" stroke="#00D4AA" strokeWidth="2" strokeLinecap="round" opacity="0.2"/>
            </svg>
            {/* Floating skill icons */}
            <div className="fl-icons">
              <svg className="fl-icon fl-icon-1" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" fill="none" stroke="#00D4AA" strokeWidth="1.5"/></svg>
              <svg className="fl-icon fl-icon-2" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" fill="none" stroke="#00D4AA" strokeWidth="1.5"/><line x1="8" y1="21" x2="16" y2="21" stroke="#00D4AA" strokeWidth="1.5"/><line x1="12" y1="17" x2="12" y2="21" stroke="#00D4AA" strokeWidth="1.5"/></svg>
              <svg className="fl-icon fl-icon-3" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="#00D4AA" strokeWidth="1.5"/><path d="M12 6v6l4 2" fill="none" stroke="#00D4AA" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </div>
            {/* Floating dots */}
            <div className="visa-dots">
              <div className="visa-dot d1"></div>
              <div className="visa-dot d2"></div>
              <div className="visa-dot d3"></div>
              <div className="visa-dot d4"></div>
              <div className="visa-dot d5"></div>
            </div>
          </div>
          <div className="service-image-overlay"></div>
        </div>
      ) : isFamilyVisa ? (
        <div className="service-image-container fv-animation-container">
          <div className="fv-animation">
            <div className="fv-bg-gradient"></div>
            {/* Family group */}
            <svg className="fv-family" viewBox="0 0 100 80">
              {/* Father */}
              <circle cx="25" cy="22" r="8" fill="rgba(255,220,185,0.6)"/>
              <rect x="17" y="32" width="16" height="20" rx="4" fill="rgba(60,130,200,0.5)"/>
              <rect x="16" y="52" width="7" height="18" rx="2" fill="rgba(40,100,170,0.4)"/>
              <rect x="27" y="52" width="7" height="18" rx="2" fill="rgba(40,100,170,0.4)"/>
              
              {/* Mother */}
              <circle cx="75" cy="22" r="8" fill="rgba(255,220,185,0.6)"/>
              <rect x="67" y="32" width="16" height="22" rx="4" fill="rgba(200,100,150,0.5)"/>
              <rect x="66" y="54" width="7" height="16" rx="2" fill="rgba(170,70,120,0.4)"/>
              <rect x="77" y="54" width="7" height="16" rx="2" fill="rgba(170,70,120,0.4)"/>
              
              {/* Child 1 */}
              <circle cx="45" cy="32" r="6" fill="rgba(255,220,185,0.6)"/>
              <rect x="39" y="40" width="12" height="14" rx="3" fill="rgba(0,180,140,0.5)"/>
              <rect x="38" y="54" width="5" height="12" rx="1.5" fill="rgba(0,150,110,0.4)"/>
              <rect x="47" y="54" width="5" height="12" rx="1.5" fill="rgba(0,150,110,0.4)"/>
              
              {/* Child 2 */}
              <circle cx="55" cy="36" r="5" fill="rgba(255,220,185,0.6)"/>
              <rect x="50" y="43" width="10" height="12" rx="2.5" fill="rgba(255,180,0,0.5)"/>
              <rect x="49" y="55" width="4" height="10" rx="1" fill="rgba(220,150,0,0.4)"/>
              <rect x="57" y="55" width="4" height="10" rx="1" fill="rgba(220,150,0,0.4)"/>
            </svg>
            {/* House silhouette */}
            <svg className="fv-house" viewBox="0 0 60 50">
              <path d="M5 25 L30 5 L55 25 L55 48 L5 48 Z" fill="rgba(0,212,170,0.15)" stroke="rgba(0,212,170,0.3)" strokeWidth="1"/>
              <rect x="12" y="30" width="10" height="12" rx="1" fill="rgba(100,220,255,0.2)"/>
              <rect x="38" y="30" width="10" height="12" rx="1" fill="rgba(100,220,255,0.2)"/>
              <rect x="24" y="33" width="12" height="15" rx="1" fill="rgba(139,69,19,0.3)"/>
              <circle cx="33" cy="40" r="1" fill="rgba(255,215,0,0.5)"/>
            </svg>
            {/* Heart icon */}
            <svg className="fv-heart" viewBox="0 0 24 24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="#00D4AA" opacity="0.6"/>
            </svg>
            {/* UAE label */}
            <div className="fv-label">
              <span>FAMILY VISA</span>
              <span className="fv-uae">UAE</span>
            </div>
            {/* Floating dots */}
            <div className="visa-dots">
              <div className="visa-dot d1"></div>
              <div className="visa-dot d2"></div>
              <div className="visa-dot d3"></div>
              <div className="visa-dot d4"></div>
            </div>
          </div>
          <div className="service-image-overlay"></div>
        </div>
      ) : isBusiness ? (
        <div className={`service-image-container biz-animation-container ${service.zone === 'mainland' ? 'is-mainland' : 'is-freezone'}`}>
          <div className="biz-animation">
            <div className="biz-bg-gradient"></div>
            <div className="biz-grid"></div>

            {/* Shimmering Dubai skyline */}
            <svg className="biz-skyline" viewBox="0 0 400 120" preserveAspectRatio="none">
              <defs>
                <linearGradient id={`bizTower-${service.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(255,215,120,0.55)" />
                  <stop offset="100%" stopColor="rgba(0,212,170,0.25)" />
                </linearGradient>
              </defs>
              <rect x="20" y="50" width="20" height="70" rx="2" fill={`url(#bizTower-${service.id})`} opacity="0.85" />
              <rect x="46" y="35" width="14" height="85" rx="2" fill="rgba(0,212,170,0.25)" />
              <rect x="66" y="58" width="18" height="62" rx="2" fill="rgba(255,215,120,0.35)" />
              <rect x="90" y="20" width="12" height="100" rx="2" fill={`url(#bizTower-${service.id})`} opacity="0.9" />
              <rect x="88" y="16" width="16" height="5" rx="1" fill="rgba(255,215,120,0.6)" />
              <rect x="110" y="45" width="18" height="75" rx="2" fill="rgba(0,212,170,0.28)" />
              <rect x="134" y="30" width="14" height="90" rx="2" fill="rgba(255,215,120,0.3)" />
              <rect x="156" y="55" width="22" height="65" rx="2" fill="rgba(0,212,170,0.22)" />
              <rect x="184" y="10" width="10" height="110" rx="1" fill={`url(#bizTower-${service.id})`} opacity="0.95" />
              <rect x="182" y="6" width="14" height="5" rx="1" fill="rgba(255,215,120,0.7)" />
              <rect x="202" y="48" width="18" height="72" rx="2" fill="rgba(0,212,170,0.26)" />
              <rect x="226" y="28" width="14" height="92" rx="2" fill="rgba(255,215,120,0.32)" />
              <rect x="248" y="60" width="20" height="60" rx="2" fill="rgba(0,212,170,0.24)" />
              <rect x="274" y="38" width="16" height="82" rx="2" fill={`url(#bizTower-${service.id})`} opacity="0.85" />
              <rect x="296" y="52" width="14" height="68" rx="2" fill="rgba(255,215,120,0.28)" />
              <rect x="316" y="24" width="10" height="96" rx="1" fill="rgba(0,212,170,0.3)" />
              <rect x="314" y="20" width="14" height="5" rx="1" fill="rgba(255,215,120,0.6)" />
              <rect x="334" y="46" width="18" height="74" rx="2" fill="rgba(0,212,170,0.24)" />
              <rect x="358" y="34" width="16" height="86" rx="2" fill="rgba(255,215,120,0.32)" />
            </svg>

            {/* Tower windows (twinkling) */}
            <div className="biz-windows">
              {Array.from({ length: 18 }).map((_, i) => (
                <span key={i} className="biz-window" style={{ animationDelay: `${(i % 9) * 0.25}s` }} />
              ))}
            </div>

            {/* Central trade-license badge */}
            <div className="biz-license">
              <div className="biz-license-shine" aria-hidden="true" />
              <div className="biz-license-inner">
                <div className="biz-license-seal">
                  {service.zone === 'mainland' ? (
                    <Building size={22} strokeWidth={1.6} />
                  ) : (
                    <Briefcase size={22} strokeWidth={1.6} />
                  )}
                </div>
                <div className="biz-license-body">
                  <div className="biz-license-title">
                    {service.zone === 'mainland' ? 'TRADE LICENSE' : 'FREE ZONE LICENSE'}
                  </div>
                  <div className="biz-license-sub">UAE · {service.zone === 'mainland' ? 'MAINLAND' : 'FREEZONE'}</div>
                  <div className="biz-license-line" />
                  <div className="biz-license-line short" />
                  <div className="biz-license-stamp">
                    <span>APPROVED</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating growth chart */}
            <svg className="biz-growth" viewBox="0 0 80 50">
              <polyline
                points="4,42 18,30 32,34 46,18 60,22 76,6"
                fill="none"
                stroke="#5EFCE8"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M68 10 L76 6 L74 14" fill="none" stroke="#5EFCE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="4" cy="42" r="2" fill="#5EFCE8" />
              <circle cx="32" cy="34" r="2" fill="#5EFCE8" />
              <circle cx="60" cy="22" r="2" fill="#5EFCE8" />
            </svg>

            {/* Floating coins */}
            <div className="biz-coins">
              <span className="biz-coin bc-1">AED</span>
              <span className="biz-coin bc-2">$</span>
              <span className="biz-coin bc-3">€</span>
            </div>

            {/* Particle dots */}
            <div className="visa-dots">
              <div className="visa-dot d1"></div>
              <div className="visa-dot d2"></div>
              <div className="visa-dot d3"></div>
              <div className="visa-dot d4"></div>
              <div className="visa-dot d5"></div>
            </div>
          </div>
          <div className="service-image-overlay"></div>
        </div>
      ) : imageUrl ? (
        <div className="service-image-container">
          <img 
            src={imageUrl} 
            alt={translatedName} 
            className="service-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="service-image-overlay"></div>
        </div>
      ) : null}
      <div className="service-card-content">
        <div className="service-icon-new">
          <IconComponent size={32} />
        </div>
        <div className="service-content">
          <h3 className="service-name-new">{translatedName}</h3>
          {translatedSubtitle && (
            <p className="service-subtitle">{translatedSubtitle}</p>
          )}
          <p className="service-description">{translatedDesc}</p>
          <div className="service-meta-new">
            <span className="service-duration">⏱️ {translateDuration(service.duration)}</span>
            <span className="service-price-new">{service.price}</span>
          </div>
        </div>
        <div className="service-buttons">
          <button 
            className="btn-primary service-book-btn" 
            onClick={() => onBookClick(service)}
          >
            {t('bookNow')}
          </button>
          {showStepsButton && (
            <button 
              className="btn-secondary service-info-btn" 
              onClick={() => onStepsClick(service)}
              data-testid={`view-steps-${service.id}`}
            >
              <Info size={18} />
              {t('viewSteps') || 'View Steps'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { stepIconMap };
export default ServiceCard;
